import { Inject, Injectable } from '@angular/core';
import { AwsRum, AwsRumConfig, TelemetryEnum } from 'aws-rum-web';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IEnvironment, IRuntimeEnvironment } from 'interfaces-lib';
import { IAnalyticsService } from './ianalyticsservice';
import { NoOpAnalyticsService } from './noop-analytics.service';

@Injectable({ providedIn: 'root' })
export class AwsRumService {
  static INFO_EVENT_TYPE = 'INFO';
  static ERROR_EVENT_TYPE = 'ERROR';

  private readonly guestRoleArn: string | undefined;
  private readonly identityPoolId: string | undefined;
  private readonly APPLICATION_ID: string | undefined;

  private awsRum: IAnalyticsService | undefined;

  constructor(
    @Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>,
    @Inject(RUNTIME_ENVIRONMENT) protected runtimeEnvironment: IRuntimeEnvironment | undefined
  ) {
    switch (runtimeEnvironment?.renderMode) {
      case 'web-app':
        this.guestRoleArn = environment.awsRum?.guestRoleArnWebApp;
        this.identityPoolId = environment.awsRum?.identityPoolIdWebApp;
        this.APPLICATION_ID = environment.awsRum?.APPLICATION_IDWebApp;
        break;
      case 'chrome-extension':
        this.guestRoleArn = environment.awsRum?.guestRoleArnChromeExt;
        this.identityPoolId = environment.awsRum?.identityPoolIdChromeExt;
        this.APPLICATION_ID = environment.awsRum?.APPLICATION_IDChromeExt;
        break;
      default:
        this.guestRoleArn = environment.awsRum?.guestRoleArn;
        this.identityPoolId = environment.awsRum?.identityPoolId;
        this.APPLICATION_ID = environment.awsRum?.APPLICATION_ID;
        break;
    }
  }

  init(): void {
    if (this.environment.enableAnalytics && this.runtimeEnvironment?.renderMode !== 'chrome-extension') {
      this.initialiseAwsRum();
    } else {
      this.awsRum = NoOpAnalyticsService;
    }
  }

  /**
   * Records a custom event in AWS RUM
   *
   * @param componentName can be found using this.constructor.name
   * @param url can be found using this.router.url (private router: Router)
   */
  // TODO: As this evolves how do we use it? Do optionals need to be optional? Do others need to be optional?
  // Could interactions be always sent, but null
  recordEventInteraction(eventType: string, componentName: string, url: string, interaction?: any): void {
    const sanitizedEventType = AwsRumService.safeEventType(eventType) ?? AwsRumService.INFO_EVENT_TYPE;
    const eventData: any = {
      srcComponent: componentName,
      url: url,
    };
    if (interaction) {
      eventData.user_interaction = interaction;
    }
    this.awsRum?.recordEvent(sanitizedEventType, eventData);
  }

  recordErrorEvent(data: any, eventType?: string): void {
    const sanitizedEventType = AwsRumService.safeEventType(eventType) ?? AwsRumService.ERROR_EVENT_TYPE;
    this.awsRum?.recordEvent(sanitizedEventType, data);
  }

  recordError(error: any) {
    this.awsRum?.recordError(error);
  }

  private initialiseAwsRum(): void {
    try {
      const config: AwsRumConfig = {
        sessionSampleRate: 1,
        guestRoleArn: this.guestRoleArn,
        identityPoolId: this.identityPoolId,
        endpoint: this.environment.awsRum?.endpoint,
        telemetries: [TelemetryEnum.Performance, TelemetryEnum.Errors, TelemetryEnum.Http],
        allowCookies: true,
        enableXRay: false,
      };

      const APPLICATION_VERSION = '1.0.0';
      const APPLICATION_REGION = this.environment.awsRum?.APPLICATION_REGION;

      if (this.APPLICATION_ID != null && APPLICATION_REGION != null) {
        this.awsRum = new AwsRum(this.APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
      }
    } catch (error) {
      // Ignore errors thrown during CloudWatch RUM web client initialization
    }
  }

  private static safeEventType(eventType?: string): string | undefined {
    return eventType?.replaceAll(' ', '_');
  }
}
